<template>
  <div 
    class="card-figu" 
    :class="{ 'card-figu--hover': hoverable, 'card-figu--clickable': clickable }"
    @click="handleClick"
  >
    <!-- Brilho effect -->
    <div class="card-figu__shine"></div>
    
    <!-- Badge (opcional) -->
    <div v-if="badge" class="card-figu__badge" :class="`card-figu__badge--${badgeType}`">
      {{ badge }}
    </div>
    
    <!-- Content -->
    <div class="card-figu__content">
      <!-- Logo/Avatar -->
      <div class="card-figu__avatar" :class="{ 'card-figu__avatar--large': largeAvatar }">
        <v-img
          v-if="image"
          :src="image"
          :alt="title"
          cover
          class="card-figu__image"
        />
        <v-icon v-else size="48" color="#003a3e">{{ icon }}</v-icon>
      </div>
      
      <!-- Title -->
      <h3 v-if="title" class="card-figu__title">{{ title }}</h3>
      
      <!-- Subtitle -->
      <p v-if="subtitle" class="card-figu__subtitle">{{ subtitle }}</p>
      
      <!-- Meta info -->
      <div v-if="meta" class="card-figu__meta">
        <slot name="meta"></slot>
      </div>
      
      <!-- Default slot -->
      <slot></slot>
    </div>
    
    <!-- Action -->
    <div v-if="$slots.action" class="card-figu__action">
      <slot name="action"></slot>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CardFigurinha',
  props: {
    title: String,
    subtitle: String,
    image: String,
    icon: {
      type: String,
      default: 'mdi-trophy'
    },
    badge: String,
    badgeType: {
      type: String,
      default: 'primary',
      validator: v => ['primary', 'success', 'warning', 'error', 'gold'].includes(v)
    },
    hoverable: {
      type: Boolean,
      default: true
    },
    clickable: {
      type: Boolean,
      default: false
    },
    largeAvatar: {
      type: Boolean,
      default: false
    }
  },
  emits: ['click'],
  methods: {
    handleClick() {
      if (this.clickable) {
        this.$emit('click')
      }
    }
  }
}
</script>

<style scoped>
/* Cores Footie */
:root {
  --footie-primary: #003a3e;
  --footie-primary-light: #006064;
  --footie-green: #2e7d32;
  --footie-gold: #ffc107;
  --footie-white: #ffffff;
}

.card-figu {
  position: relative;
  background: linear-gradient(145deg, #f5f5f5 0%, #e0e0e0 100%);
  border-radius: 16px;
  padding: 20px;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid transparent;
  background-clip: padding-box;
  box-shadow: 0 4px 15px rgba(0, 58, 62, 0.15);
}

.card-figu::before {
  content: '';
  position: absolute;
  inset: -2px;
  border-radius: 18px;
  background: linear-gradient(135deg, #003a3e, #006064, #2e7d32, #003a3e);
  background-size: 300% 300%;
  animation: gradient-rotate 4s ease infinite;
  z-index: -1;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.card-figu:hover::before,
.card-figu--hover::before {
  opacity: 1;
}

@keyframes gradient-rotate {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

/* `clickable` só diz que o cartão reage ao clique; o "levantar" é do hover.
   Antes a regra valia sem `:hover`, e o cartão clicável ficava permanentemente
   deslocado 4 px — e, pior, o `transform` cria um stacking context que põe o
   gradiente do `::before` (z-index -1) POR CIMA do fundo claro, pintando o
   cartão inteiro de verde escuro sob um título escuro. */
.card-figu--clickable {
  cursor: pointer;
}

.card-figu--hover:hover,
.card-figu--clickable:hover {
  transform: translateY(-4px);
  box-shadow: 0 15px 35px rgba(0, 58, 62, 0.25);
}

/* Shine effect */
.card-figu__shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  transition: left 0.5s ease;
}

.card-figu:hover .card-figu__shine {
  left: 100%;
}

/* Badge */
.card-figu__badge {
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.card-figu__badge--primary {
  background: #003a3e;
  color: #fff;
}

.card-figu__badge--success {
  background: #2e7d32;
  color: #fff;
}

.card-figu__badge--warning {
  background: #ff8f00;
  color: #000;
}

.card-figu__badge--error {
  background: #c62828;
  color: #fff;
}

.card-figu__badge--gold {
  background: linear-gradient(135deg, #ffc107, #ff8f00);
  color: #000;
}

/* Content */
.card-figu__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.card-figu__avatar {
  width: 80px;
  height: 80px;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  border: 3px solid #003a3e;
  box-shadow: 0 4px 12px rgba(0, 58, 62, 0.2);
}

.card-figu__avatar--large {
  width: 120px;
  height: 120px;
}

.card-figu__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.card-figu__title {
  color: #1a1a1a;
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 8px 0;
}

.card-figu__subtitle {
  color: #424242;
  font-size: 14px;
  margin: 0;
}

.card-figu__meta {
  margin-top: 12px;
  color: #616161;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

/* Action */
.card-figu__action {
  margin-top: 16px;
  width: 100%;
}

.card-figu__action :deep(.v-btn) {
  background: #003a3e !important;
  color: #fff !important;
  font-weight: 600;
  border-radius: 8px;
  text-transform: none;
  letter-spacing: 0;
}

.card-figu__action :deep(.v-btn:hover) {
  background: #006064 !important;
}
</style>
